import { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Crown, Sparkles, TrendingUp } from "lucide-react";

// Visual config for module_master.badge (MODULE_BADGE enum). Each badge has a
// deep, muted tone (see --badge-tone-* / .module-badge in App.css): white text
// on a solid fill, identical in light and dark mode and on any surface.
const BADGE_CONFIG = {
  NEW: { label: "New", tone: "new", Icon: Sparkles },
  SOON: { label: "Soon", tone: "neutral" },
  UPDATED: { label: "Updated", tone: "updated" },
  POPULAR: { label: "Popular", tone: "popular", Icon: TrendingUp },
  FEATURED: { label: "Featured", tone: "featured" },
  PREMIUM: { label: "Premium", tone: "premium", Icon: Crown },
  BETA: { label: "Beta", tone: "beta" },
  DEPRECATED: { label: "Deprecated", tone: "deprecated" },
};

export const MODULE_BADGES = Object.keys(BADGE_CONFIG);

export const getBadgeConfig = (badge) => (badge ? BADGE_CONFIG[badge] || null : null);

const BASE =
  "inline-flex flex-shrink-0 items-center gap-1 h-[18px] px-1.5 rounded-[4px] text-[9.5px] font-semibold uppercase tracking-[0.07em] leading-none select-none whitespace-nowrap";

// Inline tag placed after the module label (sidebar) or in a table cell
// (Module Master). `flex-shrink-0` so the label truncates first and the badge
// always stays fully readable.
export const ModuleBadge = ({ badge }) => {
  const config = getBadgeConfig(badge);
  if (!config) return null;
  const { label, tone, Icon } = config;

  return (
    <span data-tone={tone} className={`module-badge ${BASE}`}>
      {Icon ? (
        <Icon size={9} strokeWidth={2.5} className="flex-shrink-0" />
      ) : (
        <span className="w-[4px] h-[4px] rounded-full bg-white/85 flex-shrink-0" />
      )}
      {label}
    </span>
  );
};

// ---- Collapsed sidebar -------------------------------------------------------
// In the 80px rail the corner tag spills past the sidebar's right edge for
// longer labels ("Deprecated"), and the scrolling <nav> clips horizontally. So collapsed badges
// are portalled into a sibling overlay layer (BadgeLayer) that sits over the
// nav, clips only top/bottom (so badges scroll out of view correctly) and lets
// badges overflow past the sidebar's right edge. It lives inside the sidebar's
// own stacking context, so modals still render above it.
const BadgeLayerContext = createContext(null);

export const BadgeLayer = ({ children, enabled }) => {
  const [layerEl, setLayerEl] = useState(null);
  return (
    <BadgeLayerContext.Provider value={enabled ? layerEl : null}>
      {children}
      {enabled && (
        <div
          ref={setLayerEl}
          className="absolute inset-0 z-10 pointer-events-none"
          style={{ clipPath: "inset(0 -9999px 0 0)" }}
        />
      )}
    </BadgeLayerContext.Provider>
  );
};

// Compact corner tag ("New") pinned on the icon's top-right corner, spilling
// out to the right — the notification-tag look. Same tone + shine as the full
// badge, title-case label, no icon/dot. Render inside the icon box (`relative`).
const CORNER_TAG =
  "inline-flex items-center h-[16px] px-[5px] rounded-[4px] text-[10px] font-semibold tracking-[0.01em] leading-none whitespace-nowrap select-none";
// How far the tag starts inside the icon's right edge, and how far it rises
// above the icon's top edge.
const CORNER_INSET_X = 16;
const CORNER_RISE_Y = 8;
export const ModuleBadgeMini = ({ badge }) => {
  const layer = useContext(BadgeLayerContext);
  const anchorRef = useRef(null);
  const [pos, setPos] = useState(null);
  const config = getBadgeConfig(badge);
  const hasBadge = !!config;

  useLayoutEffect(() => {
    if (!hasBadge || !layer) return undefined;
    let frame;
    const update = () => {
      const a = anchorRef.current?.getBoundingClientRect();
      if (!a) return;
      const l = layer.getBoundingClientRect();
      setPos({
        top: a.top - l.top - CORNER_RISE_Y,
        left: a.left - l.left - CORNER_INSET_X,
      });
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(layer);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
    };
  }, [layer, hasBadge]);

  if (!hasBadge) return null;

  return (
    <>
      <span ref={anchorRef} className="absolute right-0 top-0 w-0 h-0" />
      {layer &&
        createPortal(
          // Outer span positions; the inner tag keeps .module-badge's own
          // `position: relative` (needed for the New/Premium shine).
          <span
            className="absolute flex"
            style={{
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? "visible" : "hidden",
            }}
          >
            <span data-tone={config.tone} className={`module-badge ${CORNER_TAG}`}>
              {config.label}
            </span>
          </span>,
          layer
        )}
    </>
  );
};
